import { DetailMenOnBaseType, DetailProps, DetailTypes } from "../types";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isTomorrow from "dayjs/plugin/isTomorrow";
import type { SizeType } from "shared/src/types";
import { Badge } from "components/src/components/Badge/Badge";

dayjs.extend(isToday);
dayjs.extend(isTomorrow);

export const detailPresets: Record<
  DetailTypes,
  (tag: DetailProps) => DetailProps | DetailProps[]
> = {
  "men-on-base": ({ options }: any) => {
    return ({
      showDivider: false,
      leftIcon: { name: "icon_ui_baseball_diamond", size: "lg", options, className: "cui-absolute" }
    })
  },
  "bets-placed": () => {
    return {
      leftIcon: { name: "ui_trend_up", color: "fg-inverse"},
      bgColor: "highlight-accent",
      fgColor: "fg-inverse",
      textSize: "body-xs-bold",
      padRight: true
    }
  },
  date: ({ date = "", type, asRelative, ...rest}: DetailProps & { date?: string | number; asRelative?: boolean }) => {
    const _date = !!date ? dayjs(date) : "";
    const rightNow = dayjs(new Date());
    const diff = rightNow.diff(_date, "days");

    const isToday = _date && _date.isToday();
    const isTomorrow = _date && _date.isTomorrow();
    const isValidDate = _date && _date.isValid();

    if (asRelative) {
      let relativeDay = _date && _date.format("dddd");
      if (isTomorrow) relativeDay = "Tomorrow";
      if (isToday) relativeDay = "Today";
      if (diff <= -7) relativeDay = "Next week";
      return isValidDate ? [{ tag: relativeDay, ...rest }] : [{ tag: "---", fgColor: "fg-subtle" }];
    }

    let displayedDate = _date && _date.format("ddd").toUpperCase();
    if (isTomorrow) displayedDate = "Tomorrow";
    if (isToday) displayedDate = "Today";
    if (Math.abs(diff) >= 6)
      displayedDate = _date && _date.format("MMM D").toUpperCase();
    return isValidDate ? [
      { tag: isValidDate ? displayedDate : "", ...rest },
      { tag: isValidDate ? _date.format("h:mm A") : "", showDivider: true, ...rest },
    ] : [{ tag: "---", fgColor: "fg-subtle"}, { tag: "---", fgColor: "fg-subtle"}];
  },
  live: ({ textSize, fgColor = "highlight-accent" }) => ({
    leftIcon: { name: "icon_ui_live_animated", size: 8 },
    tag: "Live",
    fgColor,
    textSize
  }),
  livestream: ({ tag, textSize }) => ({
    leftIcon: { name: "ui_watch", size: "md", color: "highlight-accent" },
    tag: tag ? tag : "Watch",
    textSize
  }),
  highlight: ({ textSize, fgColor = "highlight-accent"}) => ({
    textSize,
    fgColor
  }),
  "highlight-bg": ({textSize, bgColor = "ultramarine-300", fgColor = "highlight-accent"}) => ({
    textSize,
    bgColor,
    fgColor,
  }),
  signpost: ({textSize}) => ({
    textSize: textSize ?? "display-xs",
    fgColor: "fg-moderate",
  }),
  suspended: ({textSize}) => ({
    tag: "Suspended",
    fgColor: "status-negative",
    textSize,
  }),
  "rain-delay": ({textSize}) => ({
    tag: "Rain Delay",
    fgColor: "status-negative",
    textSize,
  }),
  "firebets": ({ textSize }) => ({
    tag: "", leftIcon: { name: "ui_fire_bets_color", size: "md" }, textSize
  }),
  "sgp": ({ size = "lg", leftIcon }) => {
    const defaultTag = { name: "ui_sgp", size: "md" as SizeType, color: "fg-primary" };
    if (typeof leftIcon === "object") return ({ tag: "", leftIcon: { ...defaultTag, ...leftIcon } });
    return {...defaultTag, leftIcon: {...defaultTag, size}}
  }
};
